import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import {
  operatorConsole,
  type ShippingPromotion,
  type ShippingPromotionInput,
  type ShippingPromotionsResponse,
} from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { useToast } from '../../components/ui/useToast';
import { backendMessage } from '../TestPlayers/backendMessage';
import { DisablePromotionDialog } from './DisablePromotionDialog';
import { PromotionForm } from './PromotionForm';
import { PromotionsTable } from './PromotionsTable';
import { browserTimeZone } from './shippingPromotions.helpers';

const NOT_ELIGIBLE_FALLBACK = 'Shipping promotions are not available for your account yet.';

// The promotions guide in the API docs.
const GUIDE_PATH = '/api#description/shipping-promotions';

// `new` opens the empty form; a promotion opens it for editing; null is closed.
type FormTarget = ShippingPromotion | 'new' | null;

/**
 * Delivery prices the operator funds for a window. The backend owns every rule
 * (overlap, eligibility, what can still be edited); this page only builds the
 * request and shows what the backend answers.
 */
export function ShippingPromotionsPage() {
  const [promotions, setPromotions] = useState<ShippingPromotion[]>([]);
  const [meta, setMeta] = useState<ShippingPromotionsResponse['meta'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [disabling, setDisabling] = useState<ShippingPromotion | null>(null);
  const { toast, show, dismiss } = useToast();

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await operatorConsole.listShippingPromotions();
      setPromotions(data.shipping_promotions);
      setMeta(data.meta);
    } catch (err) {
      setError(`Failed to load promotions: ${backendMessage(err) ?? 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave(input: ShippingPromotionInput) {
    if (formTarget === null) return;

    if (formTarget === 'new') {
      await operatorConsole.createShippingPromotion(input);
      show('success', 'Promotion created');
    } else {
      await operatorConsole.updateShippingPromotion(formTarget.id, input);
      show('success', 'Promotion saved');
    }
    setFormTarget(null);
    await load();
  }

  async function handleDisable() {
    if (!disabling) return;

    await operatorConsole.disableShippingPromotion(disabling.id);
    setDisabling(null);
    show('success', 'Promotion disabled');
    await load();
  }

  // The form needs the currency and the backend refuses an operator that does
  // not qualify, so creating waits for a list that says it may.
  const canCreate = meta?.eligible === true;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Shipping Promotions</h1>
          <p className="max-w-2xl text-[12px] text-[var(--co-muted)]">
            Players pay the promotional price for deliveries created while a promotion is active. You
            fund the difference: the part that covers our shipping and handling fees is invoiced
            monthly, and any part that comes out of your own handling markup simply reduces the
            markup you keep.
          </p>
          <Link to={GUIDE_PATH} className="mt-1 inline-block text-[12px] text-[var(--co-brand)] underline">
            Learn how promotions work →
          </Link>
        </div>
        <Button onClick={() => setFormTarget('new')} disabled={!canCreate} className="gap-2">
          <Plus size={16} aria-hidden="true" />
          New promotion
        </Button>
      </div>

      {meta && !meta.eligible && (
        <div
          role="note"
          className="max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] px-4 py-3 text-[13px] text-[var(--co-text)]"
        >
          {meta.eligibility_message ?? NOT_ELIGIBLE_FALLBACK}
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading promotions…</p>
      ) : error ? (
        <p role="alert" className="text-[13px] text-[var(--co-error)]">
          {error}
        </p>
      ) : promotions.length === 0 ? (
        <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 text-center text-[13px] text-[var(--co-muted)]">
          No promotions yet — create one to give your players a promotional delivery price.
        </div>
      ) : (
        <>
          <PromotionsTable
            promotions={promotions}
            currency={meta?.currency ?? ''}
            onEdit={setFormTarget}
            onDisable={setDisabling}
          />
          <p className="text-[11px] text-[var(--co-muted)]">
            Times are shown in your browser’s time zone: {browserTimeZone()}.
          </p>
        </>
      )}

      {formTarget && meta && (
        <PromotionForm
          key={formTarget === 'new' ? 'new' : formTarget.id}
          promotion={formTarget === 'new' ? null : formTarget}
          currency={meta.currency}
          onSubmit={handleSave}
          onClose={() => setFormTarget(null)}
        />
      )}
      {disabling && (
        <DisablePromotionDialog
          promotion={disabling}
          onConfirm={handleDisable}
          onClose={() => setDisabling(null)}
        />
      )}

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  );
}
